import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getGlobalConsumption,
  getConsumptionByModel,
  getConsumptionByOperation
} from '../services/consumption';
import { isSystemAdmin } from '../services/auth';
import DateRangeSelector from '../components/Consumption/DateRangeSelector';
import StatsCard from '../components/Consumption/StatsCard';
import BarChartWidget from '../components/Charts/BarChartWidget';
import GroupedBarChartWidget from '../components/Charts/GroupedBarChartWidget';
import AccountConsumptionPage from './AccountConsumptionPage';
import './ConsumptionDashboardPage.css';

const ConsumptionDashboardPage = () => {
  const navigate = useNavigate();
  const isSysAdmin = isSystemAdmin();

  // Date range defaults to current month
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [consumptionData, setConsumptionData] = useState([]);
  const [byModelData, setByModelData] = useState([]);
  const [byOperationData, setByOperationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calculate summary from consumption data
  const summary = {
    totalQueries: consumptionData.reduce((sum, a) => sum + (a.total_queries || 0), 0),
    totalTokens: consumptionData.reduce((sum, a) => sum + (a.total_tokens || 0), 0),
    totalInputTokens: consumptionData.reduce((sum, a) => sum + (a.total_input_tokens || 0), 0),
    totalOutputTokens: consumptionData.reduce((sum, a) => sum + (a.total_output_tokens || 0), 0),
    totalExtraCost: consumptionData.reduce((sum, a) => sum + (a.extra_cost || 0), 0)
  };

  useEffect(() => {
    if (isSysAdmin) {
      loadData();
    }
  }, [dateRange, isSysAdmin]);

  // Si no es superadmin, mostrar la pagina de consumo de cuenta
  if (!isSysAdmin) {
    return <AccountConsumptionPage />;
  }

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [consumption, byModel, byOperation] = await Promise.all([
        getGlobalConsumption(dateRange.startDate, dateRange.endDate),
        getConsumptionByModel(dateRange.startDate, dateRange.endDate),
        getConsumptionByOperation(dateRange.startDate, dateRange.endDate)
      ]);

      // Handle both array and object responses
      setConsumptionData(Array.isArray(consumption) ? consumption : consumption?.items || []);
      setByModelData(Array.isArray(byModel) ? byModel : byModel?.items || []);
      setByOperationData(Array.isArray(byOperation) ? byOperation : byOperation?.items || []);
    } catch (err) {
      setError('Error al cargar datos de consumo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountClick = (accountId) => {
    navigate(`/consumption/${accountId}`);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Prepare chart data with input/output/total breakdown
  const modelChartData = byModelData.map(m => ({
    name: m.model_name || m.model_key,
    input: m.total_input_tokens || 0,
    output: m.total_output_tokens || 0,
    total: m.total_tokens || 0
  }));

  const operationChartData = byOperationData.map(o => ({
    name: o.operation_name || o.operation_key,
    input: o.total_input_tokens || 0,
    output: o.total_output_tokens || 0,
    total: o.total_tokens || 0
  }));

  // Top 10 accounts by queries
  const top10Accounts = [...consumptionData]
    .sort((a, b) => b.total_queries - a.total_queries)
    .slice(0, 10)
    .map(a => ({
      name: a.account_name,
      value: a.total_queries
    }));

  return (
    <div className="consumption-dashboard-page">
      <div className="page-header">
        <h1>Consumo Global</h1>
        <DateRangeSelector
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={setDateRange}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatsCard
          title="Total Analisis"
          value={formatNumber(summary.totalQueries)}
          subtitle="Analisis de planos realizados"
          icon={
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          }
        />
        <StatsCard
          title="Tokens Entrada"
          value={formatNumber(summary.totalInputTokens)}
          subtitle="Input tokens consumidos"
          icon={
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 10 20 15 15 20"/>
              <path d="M4 4v7a4 4 0 004 4h12"/>
            </svg>
          }
        />
        <StatsCard
          title="Tokens Salida"
          value={formatNumber(summary.totalOutputTokens)}
          subtitle="Output tokens consumidos"
          icon={
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 10 4 15 9 20"/>
              <path d="M20 4v7a4 4 0 01-4 4H4"/>
            </svg>
          }
        />
        <StatsCard
          title="Total Tokens"
          value={formatNumber(summary.totalTokens)}
          subtitle="Todos los tokens consumidos"
          icon={
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          }
        />
        <StatsCard
          title="Coste Extra"
          value={`${summary.totalExtraCost.toFixed(2)} EUR`}
          subtitle="Por analisis adicionales"
          icon={
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
          }
        />
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <GroupedBarChartWidget
          data={modelChartData}
          title="Tokens por Modelo LLM"
          nameKey="name"
        />
        <GroupedBarChartWidget
          data={operationChartData}
          title="Tokens por Operacion"
          nameKey="name"
        />
      </div>

      <div className="charts-grid charts-grid-full">
        <BarChartWidget
          data={top10Accounts}
          title="Top 10 Cuentas por Consumo"
          dataKey="value"
          nameKey="name"
        />
      </div>

      {/* Accounts Table */}
      <div className="consumption-table-container">
        <h2>Consumo por Cuenta</h2>
        {loading && !consumptionData.length && <p>Cargando...</p>}
        <table className="consumption-table">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>Tarifa</th>
              <th>Analisis realizados</th>
              <th>Analisis incluidos</th>
              <th>Analisis extra</th>
              <th>Coste extra</th>
              <th>Tokens entrada</th>
              <th>Tokens salida</th>
              <th>Total tokens</th>
            </tr>
          </thead>
          <tbody>
            {consumptionData.map((account) => (
              <tr
                key={account.account_id}
                onClick={() => handleAccountClick(account.account_id)}
                className="clickable-row"
              >
                <td className="account-name">{account.account_name}</td>
                <td>{account.tariff_name || 'Sin tarifa'}</td>
                <td>{account.total_queries.toLocaleString()}</td>
                <td>
                  {account.queries_included === 0
                    ? 'Ilimitado'
                    : account.queries_included?.toLocaleString() || '-'}
                </td>
                <td>{account.queries_extra?.toLocaleString() || 0}</td>
                <td>
                  {account.extra_cost
                    ? `${account.extra_cost.toFixed(2)} EUR`
                    : '-'}
                </td>
                <td>{(account.total_input_tokens || 0).toLocaleString()}</td>
                <td>{(account.total_output_tokens || 0).toLocaleString()}</td>
                <td>{(account.total_tokens || 0).toLocaleString()}</td>
              </tr>
            ))}
            {!loading && consumptionData.length === 0 && (
              <tr>
                <td colSpan={9} className="empty-message">No hay datos de consumo</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsumptionDashboardPage;
